import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ModalInfo,
  SearchForm,
  SimpleModal,
  AlertMessage,
} from 'shared/components';
import { orderBy } from 'lodash';
import PropTypes from 'prop-types';
import StaffService from '../StaffService';
import InviteIndividual from './InviteIndividual';
import StaffPoolListItem from './StaffPoolListItem';

export const fetchInvitations = async (id) => {
  const personnel = await StaffService.getTestCenterPersonnel(id);
  const invitations = await StaffService.getInvitations(id);
  return [...personnel, ...invitations].map((item) => ({
    lastName: item.lastName,
    firstName: item.firstName,
    email: item.emailAddress ?? '',
    id: item.id ?? item.personnelId,
    status: item.status ?? null,

  }));
};

const StaffPoolModal = (props) => {
  const { t } = useTranslation();
  const { id, name } = props.testCenterCycle.testCenterProgram.testCenter;
  const {
    currentUserId,
    modalIsOpen,
    closeModal,
    staffActions,
    tcEvent
  } = props;
  const [invitedStaff, setInvitedStaff] = useState([]);
  const [filteredInvitedStaff, setFilteredInvitedStaff] = useState([]);
  const [searchValue, setSearchValue] = useState('');
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [confirmModalContent, setConfirmModalContent] = useState({
    id: null,
    status: '',
    header: '',
    text: '',
    buttonText: '',
  });
  const [alert, setAlert] = useState({
    message: '',
    isOpen: false,
    time: null,
    isError: false,
  });

  useEffect(() => {
    if (modalIsOpen) {
      (async () => {
        const sentInvitations = await fetchInvitations(id);
        setInvitedStaff(sentInvitations);
        setFilteredInvitedStaff(sentInvitations);
      })();
    }
  }, [modalIsOpen]);

  useEffect(() => {
    if (!searchValue) {
      setFilteredInvitedStaff(invitedStaff);
    }
  }, [invitedStaff, searchValue]);

  const handleOpenModal = (status, userId) => {
    const isPending = status === 'pending';
    setIsConfirmModalOpen(true);
    setConfirmModalContent({
      id: userId,
      status,
      header: isPending ? t('staffAndRoomsManagement.staffPool.cancelConfirm') : t('staffAndRoomsManagement.staffPool.removeConfirm'),
      text: isPending ? t('staffAndRoomsManagement.staffPool.invitee') : t('staffAndRoomsManagement.staffPool.action'),
      buttonText: isPending ? t('common.labels.cancelInvite') : t('common.labels.remove'),
    });
  };

  const handleRemove = async (removedStatus, staffId) => {
    const api = removedStatus ? 'deleteTestCenterInvitation' : 'deletePersonnelInvitation';
    try {
      await StaffService[api](staffId);
      const removedStaff = filteredInvitedStaff.filter((item) => item.id !== staffId);
      const sentInvitations = await fetchInvitations(id);
      if(removedStatus === 'pending') {
        staffActions.getInvitedEventStaff(tcEvent.id);
      }else {
        staffActions.getTcEventStaff(tcEvent.id);
        staffActions.getAvailableTcStaff(tcEvent.id, { page: 0, limit: 200 });
      }
      setInvitedStaff(sentInvitations);
      setFilteredInvitedStaff(removedStaff);
      setIsConfirmModalOpen(false);
    } catch (err) {
      setConfirmModalContent({
        ...confirmModalContent,
        header: t('errors.somethingWentWrong'),
        text: '',
        buttonText: t('common.labels.tryAgain'),
      });
    }
  };

  const handleSearch = () => {
    const searchedStaff = invitedStaff.filter((item) => {
      const fullName = item.lastName.trim()
        .concat(', ', item.firstName.trim());
      const emailAddress = item.email.trim();
      const newValue = searchValue.toLowerCase();
      return fullName.toLowerCase().includes(newValue)
      || emailAddress.toLowerCase().includes(newValue);
    });
    setFilteredInvitedStaff(searchedStaff);
  };

  const handleSearchChange = (e) => {
    setSearchValue(e.target.value);
  };

  const clearSearch = () => {
    setFilteredInvitedStaff(invitedStaff);
    setSearchValue('');
  };

  const handleCloseConfirmModal = () => {
    setIsConfirmModalOpen(false);
  };

  const handleCloseAlert = () => {
    setAlert({
      ...alert,
      isOpen: false,
    });
  };

  return (
      <ModalInfo
        modalIsOpen={modalIsOpen}
        closeModal={closeModal}
        headerTitle={t('staffAndRoomsManagement.staffPool.staffPool')}
        hideButtons
        className="staff-pool-modal"
        containerClass="staff-pool-modal"
      >
        <section className="staff-pool-container-modal">
          <AlertMessage
            show={alert.isOpen}
            time={alert.time}
            removeMessage={handleCloseAlert}
            message={alert.message}
            isError={alert.isError}
          />
          <div className="staff-container">
            <div className="staff-list-container search">
              <div>
                <SearchForm
                  className="staff-search"
                  searchValue={searchValue}
                  placeholder={t('staffAndRoomsManagement.assignStaff.searchPlaceholder')}
                  displayIconButton={false}
                  onSearch={handleSearch}
                  onChange={handleSearchChange}
                  clearSearch={clearSearch}
                />
              </div>
              <div className="results">{filteredInvitedStaff.length} results</div>
            </div>
            <div className="staff-list-container">
              {orderBy(filteredInvitedStaff, [(name) => name.firstName.toLowerCase(), (name) => name.lastName.toLowerCase()])
                .map((item, idx) =>
                  <StaffPoolListItem
                    key={idx}
                    staff={item}
                    handleRemove={handleRemove}
                    handleOpenModal={handleOpenModal}
                  />)}
            </div>
          </div>
          <div>
            <InviteIndividual
              invitedStaff={invitedStaff}
              id={id}
              name={name}
              currentUserId={currentUserId}
              setInvitedStaff={setInvitedStaff}
              setAlert={setAlert}
              staffActions={staffActions}
              tcEvent={tcEvent}
            />
          </div>
        </section>
        <SimpleModal
          isOpen={isConfirmModalOpen}
          onCancel={handleCloseConfirmModal}
          onConfirm={() => handleRemove(confirmModalContent.status, confirmModalContent.id)}
          confirmLabel={confirmModalContent.buttonText}
          cancelLabel={t('common.labels.back')}
          headline={confirmModalContent.header}
          message={confirmModalContent.text}
        />

      </ModalInfo>
  );
};

export default StaffPoolModal;

StaffPoolModal.propTypes = {
  currentUserId: PropTypes.number.isRequired,
  closeModal: PropTypes.func.isRequired,
  modalIsOpen: PropTypes.bool.isRequired,
};
